import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  message,
  Popconfirm,
  Tag,
  Typography,
  Row,
  Col,
  Alert,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import { mcpService, MCPService } from '../services/mcpService';
import './common.css';

const { Title } = Typography;

const MCPManager: React.FC = () => {
  const [loading, setLoading] = useState(false);
  
  // MCP Service相关状态
  const [mcpModalVisible, setMcpModalVisible] = useState(false);
  const [mcpForm] = Form.useForm();
  const [mcpServices, setMcpServices] = useState<MCPService[]>([]);
  const [editingService, setEditingService] = useState<MCPService | null>(null);

  useEffect(() => {
    // Set tenant from localStorage
    const currentTenant = localStorage.getItem('currentTenant') || 'default';
    mcpService.setTenant(currentTenant);
    loadMcpServices();
  }, []);

  const loadMcpServices = async () => {
    try {
      setLoading(true);
      // Ensure tenant is set before loading
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      mcpService.setTenant(currentTenant);
      const services = await mcpService.getMCPServices();
      setMcpServices(services);
    } catch (error: any) {
      message.error('Failed to load MCP services');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingService(null);
    setMcpModalVisible(true);
    mcpForm.resetFields();
    mcpForm.setFieldsValue({
      name: 'chatbi',
      database_name: 'default',
      headers: '{\n  "Content-Type": "application/json"\n}'
    });
  };

  const handleEdit = (service: MCPService) => {
    setEditingService(service);
    mcpForm.setFieldsValue({
      name: service.name,
      database_name: service.database_name || 'default',
      address: service.address,
      headers: service.headers
    });
    setMcpModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await mcpService.deleteMCPService(id);
      message.success('MCP service deleted successfully');
      loadMcpServices();
    } catch (error: any) {
      message.error('Failed to delete MCP service');
      console.error(error);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await mcpForm.validateFields();
      
      // Ensure tenant is set before submitting
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      mcpService.setTenant(currentTenant);
      
      if (editingService) {
        await mcpService.updateMCPService(editingService.id, {
          name: values.name,
          database_name: values.database_name || 'default',
          address: values.address,
          headers: values.headers
        });
        message.success('MCP service updated successfully');
      } else {
        await mcpService.createMCPService({
          name: values.name,
          database_name: values.database_name || 'default',
          address: values.address,
          headers: values.headers,
          created_by: localStorage.getItem('username') || 'admin'
        });
        message.success('MCP service created successfully');
      }
      
      setMcpModalVisible(false);
      mcpForm.resetFields();
      setEditingService(null);
      loadMcpServices();
    } catch (error: any) {
      if (!error.errorFields) {
        message.error(editingService ? 'Failed to update MCP service' : 'Failed to create MCP service');
        console.error(error);
      }
    }
  };

  const handleTestConnection = async (id: number) => {
    try {
      // Update status to testing
      setMcpServices(prev => prev.map(service => 
        service.id === id ? { ...service, status: 'testing' } : service
      ));
      
      const result = await mcpService.testConnection(id);
      
      // Update status based on result
      const newStatus = result.success ? 'connected' : 'disconnected';
      setMcpServices(prev => prev.map(service => 
        service.id === id ? { ...service, status: newStatus } : service
      ));
      
      if (result.success) {
        message.success(result.message || 'Connection successful');
      } else {
        message.error(result.message || 'Connection failed');
      }
    } catch (error: any) {
      // Update status to disconnected on error
      setMcpServices(prev => prev.map(service => 
        service.id === id ? { ...service, status: 'disconnected' } : service
      ));
      message.error('Failed to test connection');
      console.error(error);
    }
  };


  return (
    <div className="template-manager">
      <Card>
        <div className="table-header">
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4}>MCP Services Management</Title>
            </Col>
            <Col>
              <Space>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreate}
                >
                  New MCP Service
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadMcpServices}
                  loading={loading}
                >
                  Refresh
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        <Divider />

        <Table
          dataSource={mcpServices}
          columns={[
            {
              title: 'Service Name',
              dataIndex: 'name',
              key: 'name',
              width: '20%',
              align: 'left' as const,
              ellipsis: true,
            },
            {
              title: 'Database',
              dataIndex: 'database_name',
              key: 'database_name',
              width: '15%',
              align: 'left' as const,
              ellipsis: true,
              render: (database_name: string) => database_name || 'default',
            },
            {
              title: 'MCP Address',
              dataIndex: 'address',
              key: 'address',
              width: '35%',
              align: 'left' as const,
              ellipsis: true,
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              width: '12%',
              align: 'center' as const,
              render: (status: string) => {
                let color = 'default';
                let displayStatus = status || 'not_tested';
                
                switch (displayStatus) {
                  case 'connected':
                    color = 'green';
                    displayStatus = 'Connected';
                    break;
                  case 'disconnected':
                    color = 'red';
                    displayStatus = 'Disconnected';
                    break;
                  case 'not_tested':
                    color = 'gray';
                    displayStatus = 'Not Tested';
                    break;
                  case 'testing':
                    color = 'blue';
                    displayStatus = 'Testing...';
                    break;
                  default:
                    color = 'default';
                }
                
                return <Tag color={color}>{displayStatus}</Tag>;
              },
            },
            {
              title: 'Actions',
              key: 'actions',
              width: '20%',
              align: 'center' as const,
              render: (_: any, record: MCPService) => (
                <Space>
                  <Button 
                    type="link" 
                    icon={<ApiOutlined />} 
                    onClick={() => handleTestConnection(record.id)}
                    title="Test Connection"
                  >
                    Test
                  </Button>
                  <Button 
                    type="link" 
                    icon={<EditOutlined />} 
                    onClick={() => handleEdit(record)}
                  >
                    Edit
                  </Button>
                  <Popconfirm
                    title="Are you sure you want to delete this MCP service?"
                    onConfirm={() => handleDelete(record.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="link" danger icon={<DeleteOutlined />}>
                      Delete
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
          loading={loading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`,
          }}
        />
      </Card>

      {/* MCP Service弹窗 */}
      <Modal
        title={editingService ? 'Edit MCP Service' : 'New MCP Service'}
        visible={mcpModalVisible}
        onCancel={() => {
          setMcpModalVisible(false);
          mcpForm.resetFields();
          setEditingService(null);
        }}
        onOk={handleSubmit}
        width={600}
      >
        <Form
          form={mcpForm}
          layout="vertical"
        >
          <Form.Item
            label="Service Name"
            name="name"
            initialValue="chatbi"
            rules={[{ required: true, message: '请输入服务名称' }]}
          >
            <Input placeholder="请输入服务名称（允许重名）" />
          </Form.Item>

          <Form.Item
            label="Database"
            name="database_name"
            initialValue="default"
          >
            <Input placeholder="请输入数据库名称（默认：default）" />
          </Form.Item>

          <Form.Item
            name="address"
            label="ChatBI MCP Address"
            rules={[
              { required: true, message: 'Please enter MCP address' },
              { type: 'url', message: 'Please enter a valid URL' }
            ]}
          >
            <Input placeholder="e.g., https://mcp.chatbi.com:8080" />
          </Form.Item>

          <Form.Item
            name="headers"
            label="HTTP Headers (JSON format)"
            rules={[
              { required: true, message: 'Please enter HTTP headers' },
              {
                validator: (_, value) => {
                  try {
                    if (value) {
                      JSON.parse(value);
                    }
                    return Promise.resolve();
                  } catch (e) {
                    return Promise.reject(new Error('Please enter valid JSON format'));
                  }
                }
              }
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder='{\n  "Authorization": "Bearer token",\n  "Content-Type": "application/json"\n}'
            />
          </Form.Item>

          <Alert
            message="Connection Information"
            description="The MCP address should include the protocol (http/https) and port number."
            type="info"
            showIcon
          />
        </Form>
      </Modal>

    </div>
  );
};

export default MCPManager;
